/**
 * JobCard - Card de exibição de vaga de emprego
 * 
 * Extraído do App.js durante refatoração Fase 1
 * 
 * Funcionalidades:
 * - Exibição de detalhes da vaga
 * - Integração com chat para profissionais
 * - Ações de proprietário (desativar, excluir)
 * 
 * Dependências:
 * - useTranslation (traduções)
 * - useAuth (autenticação)
 * - axios (requisições API)
 * - Card components (shadcn/ui)
 * - MapPin, Clock (lucide-react)
 */

import React from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { MapPin, Clock } from "lucide-react";
import { useTranslation } from "../translations";
import { useAuth } from "../App";
import { ShareJobDialog } from "./ShareJobDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JobCard = ({ job, showEmployer = true, isOwner = false, onUpdate }) => {
  const { t } = useTranslation();
  // Get chat functions from context/parent
  const { isAuthenticated, user } = useAuth();
  const [shareOpen, setShareOpen] = React.useState(false);
  const canShare = (isOwner || (user && user.user_type === 'admin')) && !job.is_example;

  const handleStartChat = () => {
    // Dispatch custom event to open chat with employer
    const chatEvent = new CustomEvent('openChatWith', {
      detail: {
        partnerId: job.employer_id,
        partnerName: job.employer_name || 'Empregador',
        partnerType: 'employer',
        context: `Interesse na vaga: ${job.title}`
      }
    });
    window.dispatchEvent(chatEvent);
  };

  const handleDeleteJob = async () => {
    if (window.confirm("Tem certeza que deseja excluir esta vaga de emprego? Esta ação não pode ser desfeita.")) {
      try {
        await axios.delete(`${API}/jobs/${job.id}`);
        if (onUpdate) onUpdate();
        alert("Vaga excluída com sucesso!");
      } catch (error) {
        alert("Erro ao excluir vaga: " + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handleDeactivateJob = async () => {
    if (window.confirm("Deseja desativar esta vaga? Ela ficará oculta mas não será excluída permanentemente.")) {
      try {
        await axios.put(`${API}/jobs/${job.id}/deactivate`);
        if (onUpdate) onUpdate();
        alert("Vaga desativada com sucesso!");
      } catch (error) {
        alert("Erro ao desativar vaga: " + (error.response?.data?.detail || error.message));
      }
    }
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <CardTitle className="text-lg">{job.title}</CardTitle>
              {job.is_example && (
                <Badge className="bg-blue-100 text-blue-700 border border-blue-200 text-xs">
                  📘 {t('example')}
                </Badge>
              )}
            </div>
            <div className="flex items-center space-x-4 text-sm text-gray-600 mt-2">
              <div className="flex items-center">
                <MapPin className="w-4 h-4 mr-1" />
                {job.location}
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                {job.job_type}
              </div>
            </div>
          </div>
          {job.salary_range && (
            <Badge variant="outline">{job.salary_range}</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-gray-700 mb-4">{job.description}</p>
        <div className="space-y-2">
          <h4 className="font-medium">Requisitos:</h4>
          <p className="text-sm text-gray-600">{job.requirements}</p>
        </div>
        
        {/* CHAT INTEGRATION - Non-Owner Actions */}
        {!isOwner && isAuthenticated && user && user.user_type === 'professional' && (
          <div className="mt-4 pt-4 border-t">
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleStartChat}
              className="w-full bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
            >
              💬 Conversar com Empregador
            </Button>
          </div>
        )}
        
        {/* Owner Actions */}
        {isOwner && (
          <div className="mt-4 pt-4 border-t flex space-x-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleDeactivateJob}
            >
              📴 Desativar
            </Button>
            <Button 
              variant="destructive" 
              size="sm"
              onClick={handleDeleteJob}
            >
              🗑️ Excluir
            </Button>
          </div>
        )}

        {/* Divulgar nos grupos (dono da vaga ou admin) */}
        {canShare && (
          <div className={isOwner ? "mt-2" : "mt-4 pt-4 border-t"}>
            <Button
              variant="outline"
              size="sm"
              className="w-full bg-sky-50 hover:bg-sky-100 text-sky-700 border-sky-200"
              onClick={() => setShareOpen(true)}
              data-testid={`share-job-btn-${job.id}`}
            >
              📢 Divulgar nos grupos
            </Button>
            <ShareJobDialog job={job} open={shareOpen} onOpenChange={setShareOpen} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default JobCard;
